import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import UnsoldLeadDetailsApiService from '../apiService/unsoldLeadDetailsApiService';
import { UnsoldLeadDetails } from '../types';
import { SummarizationSocketService } from 'app/socketService/summarization/salesPortalSocketService';
import socketServiceManager from 'app/socketService';
import SummarizationApiService, { SummarizationInitResponse } from 'app/apiService/summarization/summarizationApiService';

interface UnsoldLeadDetailsContextType {
  leadDetails: UnsoldLeadDetails | null;
  loading: boolean;
  error: string | null;
  fetchLeadDetails: (leadId: string) => Promise<void>;
  // Lead Intelligence streaming
  leadIntelligenceStreamingText: string;
  leadIntelligenceIsStreaming: boolean;
  leadIntelligenceConversationId: string | null;
  leadIntelligenceLoading: boolean;
  leadIntelligenceError: string | null;
  initLeadIntelligence: () => Promise<void>;
  // Nurturing Strategy streaming
  nurturingStrategyStreamingText: string;
  nurturingStrategyIsStreaming: boolean;
  nurturingStrategyConversationId: string | null;
  nurturingStrategyLoading: boolean;
  nurturingStrategyError: string | null;
  initNurturingStrategy: () => Promise<void>;
}

const UnsoldLeadDetailsContext = createContext<UnsoldLeadDetailsContextType | undefined>(undefined);

export const useUnsoldLeadDetails = () => {
  const context = useContext(UnsoldLeadDetailsContext);
  if (!context) {
    throw new Error('useUnsoldLeadDetails must be used within UnsoldLeadDetailsProvider');
  }
  return context;
};

interface UnsoldLeadDetailsProviderProps {
  children: ReactNode;
  leadId: string;
}

export const UnsoldLeadDetailsProvider: React.FC<UnsoldLeadDetailsProviderProps> = ({
  children,
  leadId,
}) => {
  const [leadDetails, setLeadDetails] = useState<UnsoldLeadDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Lead Intelligence state
  const [leadIntelligenceStreamingText, setLeadIntelligenceStreamingText] = useState<string>('');
  const [leadIntelligenceIsStreaming, setLeadIntelligenceIsStreaming] = useState<boolean>(false);
  const [leadIntelligenceConversationId, setLeadIntelligenceConversationId] = useState<string | null>(null);
  const [leadIntelligenceLoading, setLeadIntelligenceLoading] = useState<boolean>(false);
  const [leadIntelligenceError, setLeadIntelligenceError] = useState<string | null>(null);

  // Nurturing Strategy state
  const [nurturingStrategyStreamingText, setNurturingStrategyStreamingText] = useState<string>('');
  const [nurturingStrategyIsStreaming, setNurturingStrategyIsStreaming] = useState<boolean>(false);
  const [nurturingStrategyConversationId, setNurturingStrategyConversationId] = useState<string | null>(null);
  const [nurturingStrategyLoading, setNurturingStrategyLoading] = useState<boolean>(false);
  const [nurturingStrategyError, setNurturingStrategyError] = useState<string | null>(null);

  // Store conversation_id from init call
  const [conversationId, setConversationId] = useState<string | null>(null);
  const initCalledRef = useRef(false);

  const selectedDealer = useSelector(selectDealerSelected);
  const dealerId = selectedDealer?.dealer || '';

  // Refs for socket service and accumulated text
  const socketServiceRef = useRef<SummarizationSocketService | null>(null);
  const isInitializedRef = useRef(false);
  const leadIntelligenceSubscriptionIdRef = useRef<string | null>(null);
  const nurturingStrategySubscriptionIdRef = useRef<string | null>(null);
  const leadIntelligenceAccumulatedTextRef = useRef<string>('');
  const nurturingStrategyAccumulatedTextRef = useRef<string>('');
  const leadIntelligenceCallbackRef = useRef<((data: any) => void) | null>(null);
  const nurturingStrategyCallbackRef = useRef<((data: any) => void) | null>(null);

  // Initialize socket service
  useEffect(() => {
    if (isInitializedRef.current) return;

    const socketConfig = {
      url: process.env.REACT_APP_API_URL || process.env.REACT_APP_WS_URL || 'http://localhost:3000',
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    };

    try {
      socketServiceManager.initializeGlobalSocket(socketConfig);
      const summarizationService = SummarizationSocketService.getInstance(socketConfig);
      socketServiceManager.registerService(summarizationService);
      socketServiceRef.current = summarizationService;

      summarizationService.on('connect', () => {
        console.log('📡 Unsold Lead Details socket connected');
      });

      summarizationService.on('disconnect', (reason) => {
        console.log('📡 Unsold Lead Details socket disconnected:', reason);
      });

      summarizationService.on('connect_error', (error) => {
        console.error('❌ Unsold Lead Details socket connection error:', error);
      });

      summarizationService.on('reconnect', (attemptNumber) => {
        console.log('📡 Unsold Lead Details socket reconnected:', attemptNumber);
      });

      summarizationService.connect();
      isInitializedRef.current = true;
      console.log('📡 Unsold Lead Details socket service initialized');
    } catch (error) {
      console.error('❌ Failed to initialize socket service:', error);
    }

    return () => {
      try {
        if (socketServiceRef.current) {
          socketServiceRef.current.disconnect();
        }
      } catch (error) {
        console.error('❌ Error disconnecting socket service:', error);
      }
    };
  }, []);

  // Create streaming callback for Lead Intelligence
  const createLeadIntelligenceCallback = useCallback((conversationId: string) => {
    return (data: any) => {
      console.log('📊 Lead Intelligence event received:', data);

      if (data.event_type === 'streaming_response' || data.message) {
        const messageObj = typeof data.message === 'object' && data.message !== null
          ? data.message
          : (typeof data === 'object' && data !== null ? data : null);

        // Check for prompt_key: "unsold_lead_summary" for Lead Intelligence
        if (
          messageObj &&
          typeof messageObj === 'object' &&
          'conversationId' in messageObj &&
          'text' in messageObj &&
          messageObj.conversationId === conversationId &&
          typeof messageObj.text === 'string' &&
          messageObj.prompt_key === 'unsold_lead_summary'
        ) {
          const newTextChunk = messageObj.text as string;

          if (!newTextChunk || newTextChunk.length === 0) {
            return;
          }

          const currentAccumulated = leadIntelligenceAccumulatedTextRef.current;

          if (currentAccumulated.endsWith(newTextChunk)) {
            console.log('⚠️ Duplicate chunk detected (ends with), skipping');
            return;
          }

          let textToAppend = newTextChunk;

          if (currentAccumulated.length > 0) {
            const maxOverlapLength = Math.min(currentAccumulated.length, newTextChunk.length);

            for (let i = maxOverlapLength; i > 0; i--) {
              const endOfAccumulated = currentAccumulated.slice(-i);
              const startOfNewChunk = newTextChunk.slice(0, i);

              if (endOfAccumulated === startOfNewChunk) {
                textToAppend = newTextChunk.slice(i);
                break;
              }
            }
          }

          if (textToAppend.length > 0) {
            leadIntelligenceAccumulatedTextRef.current = currentAccumulated + textToAppend;
            setLeadIntelligenceStreamingText(leadIntelligenceAccumulatedTextRef.current);
          }

          // Check if final before setting streaming state
          if (messageObj.isFinal === true) {
            setLeadIntelligenceIsStreaming(false);
            console.log('✅ Lead Intelligence streaming completed');
          } else {
            setLeadIntelligenceIsStreaming(true);
          }
        }
      }
    };
  }, []);

  // Create streaming callback for Nurturing Strategy
  const createNurturingStrategyCallback = useCallback((conversationId: string) => {
    return (data: any) => {
      console.log('📊 Nurturing Strategy event received:', data);

      if (data.event_type === 'streaming_response' || data.message) {
        // Extract message object - could be in data.message or data itself
        let messageObj: any = null;
        
        if (data.message && typeof data.message === 'object' && data.message !== null) {
          messageObj = data.message;
        } else if (data && typeof data === 'object' && data !== null) {
          messageObj = data;
        }

        if (!messageObj) {
          return;
        }

        // Check conversationId - handle both conversationId and conversation_id
        const msgConversationId = messageObj.conversationId || messageObj.conversation_id;
        const conversationMatches = msgConversationId === conversationId || 
                                   data.conversation_id === conversationId ||
                                   data.conversationId === conversationId;

        // Check for prompt_key: "suggested_nurturing_strategy" for Nurturing Strategy
        const promptKeyMatch = messageObj.prompt_key === 'suggested_nurturing_strategy';
        
        console.log('📊 Nurturing Strategy - Check:', {
          promptKey: messageObj.prompt_key,
          promptKeyMatch,
          msgConversationId,
          expectedConversationId: conversationId,
          conversationMatches,
          hasText: 'text' in messageObj && typeof messageObj.text === 'string',
        });

        if (
          promptKeyMatch &&
          conversationMatches &&
          'text' in messageObj &&
          typeof messageObj.text === 'string'
        ) {
          const newTextChunk = messageObj.text as string;

          if (!newTextChunk || newTextChunk.trim().length === 0) {
            return;
          }

          const currentAccumulated = nurturingStrategyAccumulatedTextRef.current;

          if (currentAccumulated.endsWith(newTextChunk)) {
            console.log('⚠️ Duplicate chunk detected (ends with), skipping');
            return;
          }

          let textToAppend = newTextChunk;

          if (currentAccumulated.length > 0) {
            const maxOverlapLength = Math.min(currentAccumulated.length, newTextChunk.length);

            for (let i = maxOverlapLength; i > 0; i--) {
              const endOfAccumulated = currentAccumulated.slice(-i);
              const startOfNewChunk = newTextChunk.slice(0, i);

              if (endOfAccumulated === startOfNewChunk) {
                textToAppend = newTextChunk.slice(i);
                break;
              }
            }
          }

          if (textToAppend.length > 0) {
            nurturingStrategyAccumulatedTextRef.current = currentAccumulated + textToAppend;
            console.log('✅ Nurturing Strategy - Updated text length:', nurturingStrategyAccumulatedTextRef.current.length);
            setNurturingStrategyStreamingText(nurturingStrategyAccumulatedTextRef.current);
          }

          // Check if final before setting streaming state
          if (messageObj.isFinal === true) {
            setNurturingStrategyIsStreaming(false);
            console.log('✅ Nurturing Strategy streaming completed');
          } else {
            setNurturingStrategyIsStreaming(true);
          }
        }
      }
    };
  }, []);

  // Initialize Lead Intelligence - uses shared conversation_id
  const initLeadIntelligence = useCallback(async (): Promise<void> => {
    if (!conversationId) {
      setLeadIntelligenceError('Conversation ID is required');
      return;
    }

    if (leadIntelligenceConversationId === conversationId) {
      return; // Already initialized with this conversation_id
    }

    setLeadIntelligenceLoading(true);
    setLeadIntelligenceError(null);
    try {
      setLeadIntelligenceConversationId(conversationId);
      setLeadIntelligenceStreamingText('');
      leadIntelligenceAccumulatedTextRef.current = '';
      console.log('Lead Intelligence conversation_id set:', conversationId);

      try {
        const service = socketServiceRef.current || socketServiceManager.getService<SummarizationSocketService>('SummarizationSocketService');

        if (service) {
          const subscriptionId = `leadIntelligence-${conversationId}`;
          if (leadIntelligenceSubscriptionIdRef.current === subscriptionId && leadIntelligenceCallbackRef.current) {
            console.log('📡 Already subscribed to Lead Intelligence conversation');
            return;
          }

          const callback = createLeadIntelligenceCallback(conversationId);
          leadIntelligenceCallbackRef.current = callback;

          if (!service.isConnected()) {
            console.log('📡 Socket not connected, attempting to connect...');
            service.connect();

            setTimeout(() => {
              if (service.isConnected()) {
                leadIntelligenceSubscriptionIdRef.current = subscriptionId;
                service.subscribeToSummarization(subscriptionId, callback, {
                  conversation_id: conversationId,
                  subscription_type: 'conversation'
                });
                console.log('📡 Subscribed to Lead Intelligence events for conversation:', conversationId);
              } else {
                leadIntelligenceSubscriptionIdRef.current = subscriptionId;
                service.subscribeToSummarization(subscriptionId, callback, {
                  conversation_id: conversationId,
                  subscription_type: 'conversation'
                });
              }
            }, 500);
          } else {
            leadIntelligenceSubscriptionIdRef.current = subscriptionId;
            service.subscribeToSummarization(subscriptionId, callback, {
              conversation_id: conversationId,
              subscription_type: 'conversation'
            });
            console.log('📡 Subscribed to Lead Intelligence events for conversation:', conversationId);
          }
        }
      } catch (socketError: any) {
        console.warn('⚠️ Socket service subscription failed:', socketError);
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'An error occurred while initializing Lead Intelligence';
      setLeadIntelligenceError(errorMessage);
      console.error('Init Lead Intelligence error:', err);
    } finally {
      setLeadIntelligenceLoading(false);
    }
  }, [conversationId, leadIntelligenceConversationId, createLeadIntelligenceCallback]);

  // Initialize Nurturing Strategy - uses shared conversation_id
  const initNurturingStrategy = useCallback(async (): Promise<void> => {
    if (!conversationId) {
      setNurturingStrategyError('Conversation ID is required');
      return;
    }

    if (nurturingStrategyConversationId === conversationId) {
      return; // Already initialized with this conversation_id
    }

    setNurturingStrategyLoading(true);
    setNurturingStrategyError(null);
    try {
      setNurturingStrategyConversationId(conversationId);
      setNurturingStrategyStreamingText('');
      nurturingStrategyAccumulatedTextRef.current = '';
      console.log('Nurturing Strategy conversation_id set:', conversationId);

      try {
        const service = socketServiceRef.current || socketServiceManager.getService<SummarizationSocketService>('SummarizationSocketService');

        if (service) {
          const subscriptionId = `nurturingStrategy-${conversationId}`;
          if (nurturingStrategySubscriptionIdRef.current === subscriptionId && nurturingStrategyCallbackRef.current) {
            console.log('📡 Already subscribed to Nurturing Strategy conversation');
            return;
          }

          const callback = createNurturingStrategyCallback(conversationId);
          nurturingStrategyCallbackRef.current = callback;

          if (!service.isConnected()) {
            console.log('📡 Socket not connected, attempting to connect...');
            service.connect();

            setTimeout(() => {
              if (service.isConnected()) {
                nurturingStrategySubscriptionIdRef.current = subscriptionId;
                service.subscribeToSummarization(subscriptionId, callback, {
                  conversation_id: conversationId,
                  subscription_type: 'conversation'
                });
                console.log('📡 Subscribed to Nurturing Strategy events for conversation:', conversationId);
              } else {
                nurturingStrategySubscriptionIdRef.current = subscriptionId;
                service.subscribeToSummarization(subscriptionId, callback, {
                  conversation_id: conversationId,
                  subscription_type: 'conversation'
                });
              }
            }, 500);
          } else {
            nurturingStrategySubscriptionIdRef.current = subscriptionId;
            service.subscribeToSummarization(subscriptionId, callback, {
              conversation_id: conversationId,
              subscription_type: 'conversation'
            });
            console.log('📡 Subscribed to Nurturing Strategy events for conversation:', conversationId);
          }
        }
      } catch (socketError: any) {
        console.warn('⚠️ Socket service subscription failed:', socketError);
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'An error occurred while initializing Nurturing Strategy';
      setNurturingStrategyError(errorMessage);
      console.error('Init Nurturing Strategy error:', err);
    } finally {
      setNurturingStrategyLoading(false);
    }
  }, [conversationId, nurturingStrategyConversationId, createNurturingStrategyCallback]);

  // Subscribe to socket when conversation_id is available
  useEffect(() => {
    if (leadIntelligenceConversationId) {
      const service = socketServiceRef.current || socketServiceManager.getService<SummarizationSocketService>('SummarizationSocketService');
      const subscriptionId = `leadIntelligence-${leadIntelligenceConversationId}`;

      if (!service || leadIntelligenceSubscriptionIdRef.current === subscriptionId && leadIntelligenceCallbackRef.current) {
        return;
      }

      if (!service.isConnected()) {
        service.connect();
        const connectHandler = () => {
          const callback = createLeadIntelligenceCallback(leadIntelligenceConversationId);
          leadIntelligenceCallbackRef.current = callback;
          leadIntelligenceSubscriptionIdRef.current = subscriptionId;
          service.subscribeToSummarization(subscriptionId, callback, {
            conversation_id: leadIntelligenceConversationId,
            subscription_type: 'conversation'
          });
          service.off('connect', connectHandler);
        };
        service.on('connect', connectHandler);

        setTimeout(() => {
          if (service.isConnected() && !leadIntelligenceCallbackRef.current) {
            const callback = createLeadIntelligenceCallback(leadIntelligenceConversationId);
            leadIntelligenceCallbackRef.current = callback;
            leadIntelligenceSubscriptionIdRef.current = subscriptionId;
            service.subscribeToSummarization(subscriptionId, callback, {
              conversation_id: leadIntelligenceConversationId,
              subscription_type: 'conversation'
            });
            service.off('connect', connectHandler);
          }
        }, 500);
      } else {
        const callback = createLeadIntelligenceCallback(leadIntelligenceConversationId);
        leadIntelligenceCallbackRef.current = callback;
        leadIntelligenceSubscriptionIdRef.current = subscriptionId;
        service.subscribeToSummarization(subscriptionId, callback, {
          conversation_id: leadIntelligenceConversationId,
          subscription_type: 'conversation'
        });
      }
    }
  }, [leadIntelligenceConversationId, createLeadIntelligenceCallback]);

  useEffect(() => {
    if (nurturingStrategyConversationId) {
      const service = socketServiceRef.current || socketServiceManager.getService<SummarizationSocketService>('SummarizationSocketService');
      const subscriptionId = `nurturingStrategy-${nurturingStrategyConversationId}`;

      if (!service || nurturingStrategySubscriptionIdRef.current === subscriptionId && nurturingStrategyCallbackRef.current) {
        return;
      }

      if (!service.isConnected()) {
        service.connect();
        const connectHandler = () => {
          const callback = createNurturingStrategyCallback(nurturingStrategyConversationId);
          nurturingStrategyCallbackRef.current = callback;
          nurturingStrategySubscriptionIdRef.current = subscriptionId;
          service.subscribeToSummarization(subscriptionId, callback, {
            conversation_id: nurturingStrategyConversationId,
            subscription_type: 'conversation'
          });
          service.off('connect', connectHandler);
        };
        service.on('connect', connectHandler);

        setTimeout(() => {
          if (service.isConnected() && !nurturingStrategyCallbackRef.current) {
            const callback = createNurturingStrategyCallback(nurturingStrategyConversationId);
            nurturingStrategyCallbackRef.current = callback;
            nurturingStrategySubscriptionIdRef.current = subscriptionId;
            service.subscribeToSummarization(subscriptionId, callback, {
              conversation_id: nurturingStrategyConversationId,
              subscription_type: 'conversation'
            });
            service.off('connect', connectHandler);
          }
        }, 500);
      } else {
        const callback = createNurturingStrategyCallback(nurturingStrategyConversationId);
        nurturingStrategyCallbackRef.current = callback;
        nurturingStrategySubscriptionIdRef.current = subscriptionId;
        service.subscribeToSummarization(subscriptionId, callback, {
          conversation_id: nurturingStrategyConversationId,
          subscription_type: 'conversation'
        });
      }
    }
  }, [nurturingStrategyConversationId, createNurturingStrategyCallback]);

  // Initialize conversation_id first
  useEffect(() => {
    const initializeConversation = async () => {
      if (!dealerId || initCalledRef.current || conversationId) {
        return;
      }

      initCalledRef.current = true;
      try {
        console.log('🚀 Initializing conversation for Lead Intelligence and Nurturing Strategy...');
        const initResponse = await SummarizationApiService.init(dealerId);
        if (initResponse.body?.conversation_id) {
          setConversationId(initResponse.body.conversation_id);
          console.log('📊 Conversation initialized, conversation_id:', initResponse.body.conversation_id);
        }
      } catch (error) {
        console.error('❌ Failed to initialize conversation:', error);
        initCalledRef.current = false; // Allow retry
      }
    };

    initializeConversation();
  }, [dealerId, conversationId]);

  const fetchLeadDetails = async (id: string, convId?: string | null) => {
    try {
      setLoading(true);
      setError(null);
      const response = await UnsoldLeadDetailsApiService.getLeadDetails(id, dealerId, convId || undefined);
      if (response.body) {
        setLeadDetails(response.body);
      }
    } catch (err) {
      console.error('Error fetching lead details:', err);
      setError('Failed to load lead details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch lead details after conversation_id is available
  useEffect(() => {
    if (leadId && conversationId) {
      console.log('📡 Fetching lead details with conversation_id:', conversationId);
      fetchLeadDetails(leadId, conversationId);
      
      // Initialize both Lead Intelligence and Nurturing Strategy with the conversation_id
      initLeadIntelligence();
      initNurturingStrategy();
    }
  }, [leadId, conversationId, initLeadIntelligence, initNurturingStrategy]);

  return (
    <UnsoldLeadDetailsContext.Provider
      value={{
        leadDetails,
        loading,
        error,
        fetchLeadDetails,
        leadIntelligenceStreamingText,
        leadIntelligenceIsStreaming,
        leadIntelligenceConversationId,
        leadIntelligenceLoading,
        leadIntelligenceError,
        initLeadIntelligence,
        nurturingStrategyStreamingText,
        nurturingStrategyIsStreaming,
        nurturingStrategyConversationId,
        nurturingStrategyLoading,
        nurturingStrategyError,
        initNurturingStrategy,
      }}
    >
      {children}
    </UnsoldLeadDetailsContext.Provider>
  );
};

